import React from "react";
import { useNavigate } from "react-router-dom";
import FooterOne from "../../footer/FooterOne";
import Head from "../../components/Head";
import HeaderOne from "../../header/HeaderOne";

const AffiliateProgram = () => {
  const navigate = useNavigate();

  return (
    <>
      <Head title="Affiliate Program — Earn 30% Recurring on SMS Gateway" />
      <HeaderOne />

      <main className="aff">
        <div className="aff-container">

          {/* ---- HERO ---- */}
          <header className="aff-hero">
            <span className="aff-label">AFFILIATE PROGRAM</span>
            <h1>Earn 30% recurring commission on every referral</h1>
            <p>
              Share TextLink with your audience and earn 30% of their monthly subscription — every
              month, for as long as they stay. No caps, no tiers, no complexity.
            </p>
            <button className="aff-btn-primary" onClick={() => navigate("/signup")}>
              Join the Program <span className="aff-arrow">→</span>
            </button>
          </header>

          {/* ---- HIGHLIGHTS ---- */}
          <section className="aff-highlights">
            <div className="aff-highlight">
              <div className="aff-highlight-value">30%</div>
              <div className="aff-highlight-label">Recurring commission</div>
            </div>
            <div className="aff-highlight-divider"></div>
            <div className="aff-highlight">
              <div className="aff-highlight-value">$50</div>
              <div className="aff-highlight-label">Minimum payout</div>
            </div>
            <div className="aff-highlight-divider"></div>
            <div className="aff-highlight">
              <div className="aff-highlight-value">Lifetime</div>
              <div className="aff-highlight-label">Cookie duration</div>
            </div>
            <div className="aff-highlight-divider"></div>
            <div className="aff-highlight">
              <div className="aff-highlight-value">Monthly</div>
              <div className="aff-highlight-label">Payouts</div>
            </div>
          </section>

          {/* ---- HOW IT WORKS ---- */}
          <section className="aff-section">
            <span className="aff-label">HOW IT WORKS</span>
            <h2>Three steps to start earning</h2>
            <div className="aff-steps">
              <div className="aff-step">
                <span className="aff-badge">1</span>
                <h3>Sign up & get your link</h3>
                <p>
                  Create a free TextLink account. Your unique affiliate link is waiting in the
                  dashboard — ready to share immediately.
                </p>
              </div>
              <div className="aff-step">
                <span className="aff-badge">2</span>
                <h3>Share with your audience</h3>
                <p>
                  Post your link on social media, embed it in tutorials, mention it in your
                  community, or send it directly to anyone who sends SMS at scale.
                </p>
              </div>
              <div className="aff-step">
                <span className="aff-badge">3</span>
                <h3>Earn every month</h3>
                <p>
                  When someone signs up through your link and subscribes, you earn 30% of their
                  plan — every billing cycle, for as long as they're a customer. Withdraw once
                  you hit $50.
                </p>
              </div>
            </div>
          </section>

          {/* ---- WHO IS IT FOR ---- */}
          <section className="aff-section">
            <span className="aff-label">WHO IT'S FOR</span>
            <h2>Great fit if you're any of these</h2>
            <div className="aff-who-grid">
              <div className="aff-who-card">
                <div className="aff-who-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                </div>
                <h3>Agency owners</h3>
                <p>Already selling marketing services? Add TextLink as a recommended tool and earn on every client you onboard.</p>
              </div>
              <div className="aff-who-card">
                <div className="aff-who-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                </div>
                <h3>Content creators</h3>
                <p>Review TextLink on YouTube, write a blog post, or mention it in a newsletter — your audience trusts your recommendations.</p>
              </div>
              <div className="aff-who-card">
                <div className="aff-who-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                </div>
                <h3>Developers & consultants</h3>
                <p>Building SMS into a client project? Recommend TextLink and earn recurring revenue alongside your consulting fees.</p>
              </div>
              <div className="aff-who-card">
                <div className="aff-who-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                </div>
                <h3>Community leaders</h3>
                <p>Run a Discord, Slack, or Facebook group for marketers or GHL users? Share your link where people already ask about SMS solutions.</p>
              </div>
            </div>
          </section>

          {/* ---- EXAMPLE EARNINGS ---- */}
          <section className="aff-section">
            <span className="aff-label">EARNING POTENTIAL</span>
            <h2>What your referrals could look like</h2>
            <div className="aff-earnings-table">
              <div className="aff-earnings-row aff-earnings-header">
                <span>Referrals on $99/mo plan</span>
                <span>Your monthly earnings</span>
              </div>
              <div className="aff-earnings-row">
                <span>5 customers</span>
                <span className="aff-earnings-amount">$198/mo</span>
              </div>
              <div className="aff-earnings-row">
                <span>15 customers</span>
                <span className="aff-earnings-amount">$594/mo</span>
              </div>
              <div className="aff-earnings-row">
                <span>50 customers</span>
                <span className="aff-earnings-amount">$1,980/mo</span>
              </div>
              <div className="aff-earnings-row">
                <span>100 customers</span>
                <span className="aff-earnings-amount">$3,960/mo</span>
              </div>
            </div>
            <p className="aff-earnings-note">
              Earnings scale with plan size. Referrals on the $259/mo plan earn you $103.60 each per month.
            </p>
          </section>

          {/* ---- CTA ---- */}
          <section className="aff-cta">
            <h2>Start earning today</h2>
            <p>Sign up, grab your link, and share it. That's it — no approval process, no waiting.</p>
            <button className="aff-btn-primary" onClick={() => navigate("/signup")}>
              Join the Affiliate Program <span className="aff-arrow">→</span>
            </button>
          </section>

        </div>
      </main>

      <FooterOne />

      <style jsx>{`
        .aff {
          padding: clamp(8rem, 14vh, 12rem) 0 clamp(3rem, 6vh, 5rem);
          background: #fafafa;
        }
        .aff-container {
          max-width: 960px;
          margin: 0 auto;
          padding: 0 clamp(1.25rem, 3vw, 2rem);
          box-sizing: border-box;
        }

        .aff-label {
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #71717a;
          display: block;
          margin-bottom: 0.75rem;
        }

        /* hero */
        .aff-hero {
          text-align: center;
          margin-bottom: clamp(3rem, 6vh, 5rem);
        }
        .aff-hero h1 {
          font-size: clamp(1.75rem, 4vw, 2.5rem);
          font-weight: 600;
          color: #18181b;
          letter-spacing: -0.04em;
          line-height: 1.15;
          margin-bottom: 1.25rem;
        }
        .aff-hero p {
          font-size: clamp(0.9375rem, 1.5vw, 1.0625rem);
          color: #71717a;
          max-width: 36rem;
          margin: 0 auto 2rem;
          line-height: 1.7;
        }

        /* buttons */
        .aff-btn-primary {
          background: #18181b;
          color: #fff;
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-weight: 500;
          font-size: 0.9375rem;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          transition: all 0.15s ease;
        }
        .aff-btn-primary:hover {
          background: #27272a;
          transform: translateY(-1px);
        }
        .aff-arrow {
          transition: transform 0.15s ease;
        }
        .aff-btn-primary:hover .aff-arrow {
          transform: translateX(2px);
        }

        /* highlights */
        .aff-highlights {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: clamp(1.5rem, 4vw, 3rem);
          flex-wrap: wrap;
          padding: clamp(2rem, 4vh, 3rem) clamp(1.5rem, 3vw, 2rem);
          background: #fff;
          border-radius: 14px;
          border: 1px solid #e4e4e7;
          margin-bottom: clamp(4rem, 8vh, 6rem);
        }
        .aff-highlight {
          text-align: center;
        }
        .aff-highlight-value {
          font-size: clamp(1.5rem, 3vw, 2rem);
          font-weight: 600;
          color: #18181b;
          letter-spacing: -0.03em;
          line-height: 1;
          margin-bottom: 0.375rem;
        }
        .aff-highlight-label {
          font-size: 0.8125rem;
          color: #71717a;
        }
        .aff-highlight-divider {
          width: 1px;
          height: 32px;
          background: #e4e4e7;
        }

        /* sections */
        .aff-section {
          margin-bottom: clamp(4rem, 8vh, 6rem);
        }
        .aff-section h2 {
          font-size: clamp(1.5rem, 3vw, 2rem);
          font-weight: 600;
          color: #18181b;
          letter-spacing: -0.03em;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        /* steps */
        .aff-steps {
          display: grid;
          gap: 1rem;
        }
        @media (min-width: 768px) {
          .aff-steps {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        .aff-step {
          background: #fff;
          border: 1px solid #e4e4e7;
          border-radius: 14px;
          padding: clamp(1.5rem, 3vw, 2rem);
          transition: all 0.15s ease;
        }
        .aff-step:hover {
          border-color: #d4d4d8;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
        }
        .aff-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.25rem;
          height: 2.25rem;
          border-radius: 10px;
          background: #18181b;
          color: #fff;
          font-weight: 600;
          margin-bottom: 1rem;
          font-size: 1rem;
        }
        .aff-step h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #18181b;
          margin-bottom: 0.5rem;
          letter-spacing: -0.01em;
        }
        .aff-step p {
          font-size: 0.9375rem;
          color: #71717a;
          line-height: 1.7;
          margin: 0;
        }

        /* who grid */
        .aff-who-grid {
          display: grid;
          gap: 1rem;
        }
        @media (min-width: 768px) {
          .aff-who-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        .aff-who-card {
          background: #fff;
          border: 1px solid #e4e4e7;
          border-radius: 14px;
          padding: clamp(1.5rem, 3vw, 2rem);
          transition: all 0.15s ease;
        }
        .aff-who-card:hover {
          border-color: #d4d4d8;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
        }
        .aff-who-icon {
          color: #18181b;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
        }
        .aff-who-card h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #18181b;
          margin-bottom: 0.5rem;
          letter-spacing: -0.01em;
        }
        .aff-who-card p {
          font-size: 0.9375rem;
          color: #71717a;
          line-height: 1.7;
          margin: 0;
        }

        /* earnings table */
        .aff-earnings-table {
          background: #fff;
          border: 1px solid #e4e4e7;
          border-radius: 14px;
          overflow: hidden;
        }
        .aff-earnings-row {
          display: flex;
          justify-content: space-between;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f4f4f5;
          font-size: 0.9375rem;
          color: #71717a;
        }
        .aff-earnings-row:last-child {
          border-bottom: none;
        }
        .aff-earnings-header {
          background: #fafafa;
          font-size: 0.8125rem;
          font-weight: 600;
          color: #52525b;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .aff-earnings-amount {
          font-weight: 600;
          color: #18181b;
        }
        .aff-earnings-note {
          font-size: 0.8125rem;
          color: #a1a1aa;
          margin-top: 1rem;
        }

        /* cta */
        .aff-cta {
          text-align: center;
          padding: clamp(2.5rem, 5vw, 4rem) clamp(1.5rem, 3vw, 2rem);
          border-radius: 14px;
          background: #fff;
          border: 1px solid #e4e4e7;
        }
        .aff-cta h2 {
          font-size: clamp(1.5rem, 3vw, 1.75rem);
          margin: 0 0 0.75rem;
          font-weight: 600;
          color: #18181b;
          letter-spacing: -0.03em;
        }
        .aff-cta p {
          color: #71717a;
          margin: 0 0 2rem;
          font-size: 1rem;
        }

        /* mobile */
        @media (max-width: 767px) {
          .aff {
            padding: 7rem 0 2rem;
          }
          .aff-hero h1 {
            font-size: 1.5rem;
          }
          .aff-highlight-divider {
            display: none;
          }
          .aff-highlight {
            flex: 1 1 45%;
            min-width: 120px;
          }
          .aff-earnings-row {
            padding: 0.875rem 1rem;
            font-size: 0.875rem;
          }
        }
      `}</style>
    </>
  );
};

export default AffiliateProgram;
